import React, { useCallback } from 'react'

import type { AdapterKitControlProps } from 'components/formKit/types'
import type { IIconButtonProps } from 'components/uiKit/Button'
import { IconButton } from 'components/uiKit/Button'
import { Tooltip } from 'components/uiKit/Dropdown'
import type { KeyIconsType } from 'components/uiKit/Icon'
import { isMixed } from 'components/uiKit/KitUtils'

interface IFieldIconButtonProps<V extends string | number | boolean>
  extends Omit<IIconButtonProps, 'value'> {
  truthy: { value: V; icon?: KeyIconsType }
  falsy: { value: V; icon?: KeyIconsType }
  tooltip?: React.ReactNode
}

const FieldIconButton = <V extends string | number | boolean>({
  name,
  defaultValue,
  value = defaultValue,
  falsy,
  truthy,
  onChange,
  tooltip,
  error,
  ...rest
}: AdapterKitControlProps<IFieldIconButtonProps<V>, V>) => {
  const mixed = isMixed(value)
  const isActive = !mixed && value === truthy.value
  const icon = (isActive ? truthy.icon : falsy.icon) || rest.icon
  const handleToggle = useCallback(() => {
    const newValue = value === truthy.value ? falsy.value : truthy.value
    onChange?.({ name, value: newValue })
  }, [onChange, name, falsy, truthy, value])

  return (
    <Tooltip mouseEnterDelay={0.5} overlay={tooltip} placement='top'>
      <IconButton
        {...rest}
        active={isActive}
        icon={icon}
        name={name}
        onClick={handleToggle}
        selected={isActive}
      />
    </Tooltip>
  )
}

export default React.memo(FieldIconButton) as typeof FieldIconButton
